import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import io from "socket.io-client";

import { Box, Button, Typography } from "@mui/material";
import { Mic, MicOff } from "@mui/icons-material";

const sampleRateHertz = 16000;

const getMediaStream = async () =>
	navigator.mediaDevices.getUserMedia({
		audio: {
			deviceId: "default",
			sampleRate: sampleRateHertz,
			sampleSize: 16,
			channelCount: 1,
		},
		video: false,
	});

const encoding = "LINEAR16";
const languageCode = "en-US";
const request = {
	config: {
		encoding: encoding,
		sampleRateHertz: sampleRateHertz,
		languageCode: languageCode,
		profanityFilter: true,
		enableAutomaticPunctuation: true,
		diarizationConfig: {
			enableSpeakerDiarization: true,
			minSpeakerCount: 2,
			maxSpeakerCount: 2, // TODO: set to number of attendees
		},
		model: "video",
		speechContexts: [],
	},
	interimResults: false, // if no interim results, set to false
};

const SOCKET_URL = `${process.env.REACT_APP_PORT}`;

const Recorder = ({ onTranscriptChange }) => {
	const socket = useRef(null);
	const recorder = useRef(null);
	const audioStream = useRef(null);
	const audioContext = useRef(null);
	const audioInput = useRef(null);

	const isRecordingRef = useRef(false);
	const [isRecording, setIsRecording] = useState(false);

	const openSocket = () => {
		// Connect to the server using socket.io
		socket.current = io(SOCKET_URL);

		console.log("Connected to server via socket...");

		// handle incoming transcription
		socket.current.on("transcription", (transcript) => {
			onTranscriptChange(transcript);

			// if the transcription is final, close the socket
			if (!isRecordingRef.current) {
				socket.current.emit("endGoogleSpeechStream", "");
				closeSocket();
			}
		});

		// Handle disconnection
		socket.current.on("disconnect", () => {
			console.log("Disconnected from server");
			socket.current = null;
		});
	};

	const closeSocket = () => {
		// Disconnect from the server
		console.log("Disconnecting from server...");
		if (socket.current) {
			socket.current.disconnect();
			socket.current = null;
		}
	};

	// setup stream
	const onSuccess = async (stream) => {
		audioStream.current = stream;

		// set the recorder to be recording
		setIsRecording(true);
		isRecordingRef.current = true;

		//init socket Google Speech Connection
		socket.current.emit("startGoogleSpeechStream", request);

		// initialize audio context
		const AudioContext = window.AudioContext || window.webkitAudioContext;
		audioContext.current = new AudioContext();

		// initialize audio worklet
		await audioContext.current.audioWorklet.addModule(
			"/src/worklets/recorderWorkletProcessor.js"
		);
		audioContext.current.resume();

		// create the audio Input
		audioInput.current = audioContext.current.createMediaStreamSource(stream);

		// create the recorder from our worklet
		recorder.current = new AudioWorkletNode(
			audioContext.current,
			"recorder.worklet"
		);

		// hook up the audio Input, context, and recorder
		audioInput.current.connect(recorder.current);
		recorder.current.connect(audioContext.current.destination);
		audioContext.current.resume();

		recorder.current.port.onmessage = (event) => {
			// send audio to socket server
			const audioData = event.data;
			socket.current.emit("binaryAudioData", audioData);
		};
		console.log("recorder is connected");
	};

	const initRecording = async () => {
		if (!recorder.current) {
			await getMediaStream()
				.then(onSuccess)
				.catch((err) => console.log("Uh oh... unable to get stream...", err));
		} else console.log("recorder already connected");
	};

	const startRecording = async () => {
		try {
			// connect to socket server and send request to start Google Speech Stream
			openSocket();
			// connect to audio stream
			await initRecording();
		} catch (err) {
			console.log(err);
			socket.current.emit("endGoogleSpeechStream", "");
			closeSocket();
		}
	};

	const stopRecording = () => {
		console.log("recorder is stopping");
		halfClose();
	};

	// half-close the write (audio input) stream
	const halfClose = () => {
		if (audioStream) {
			audioStream.current.getTracks().forEach((track) => track.stop());
		}

		if (recorder) {
			if (audioInput) {
				audioInput.current.disconnect(recorder);
			}
			recorder.current.disconnect(audioContext.current.destination);
		}

		if (audioContext) {
			audioContext.current.close();
			audioInput.current = null;
			recorder.current = null;
			audioContext.current = null;
		}

		setIsRecording(false);
		isRecordingRef.current = false;
	};

	return (
		/*<div>
            <button onClick={startRecording}>Start</button>
            <button onClick={stopRecording}>Stop</button>
            <p>{transcription}</p>
        </div>*/

		<Button
			variant="contained"
			endIcon={isRecording ? <MicOff /> : <Mic />}
			sx={{ textTransform: "none" }}
			color={isRecording ? "error" : "primary"}
			onClick={isRecording ? stopRecording : startRecording}
		>
			<Typography variant="secondary">
				{isRecording ? "Stop" : "Start"} Recording
			</Typography>
		</Button>
	);
};

export default Recorder;
